/**
 * ListRow Component
 *
 * A reusable row component for list views that handles both desktop and mobile layouts.
 * Provides consistent styling and responsive behavior across the application.
 *
 * @example
 * ```tsx
 * <ListRow
 *   desktopContent={
 *     <div className="flex items-center">
 *       <span>{item.date}</span>
 *       <span>{item.name}</span>
 *     </div>
 *   }
 *   mobileContent={
 *     <ListMobileCard
 *       primaryText={item.name}
 *       secondaryText={item.email}
 *       badges={[{ label: 'Active', color: 'green' }]}
 *       actions={actions}
 *     />
 *   }
 * />
 * ```
 */
import React from 'react';

export interface ListRowProps {
  /** Content to render on desktop (md and above) */
  desktopContent: React.ReactNode;
  /** Content to render on mobile (below md breakpoint) */
  mobileContent: React.ReactNode;
  /** Optional click handler for the entire row */
  onClick?: () => void;
  /** Optional CSS class for the container */
  className?: string;
  /** Whether the row is selected/active */
  isSelected?: boolean;
}

const ListRow: React.FC<ListRowProps> = ({
  desktopContent,
  mobileContent,
  onClick,
  className = '',
  isSelected = false,
}) => {
  const baseClasses = `${isSelected ? 'bg-slate-100 border-slate-400' : 'bg-white border-gray-200'} border rounded-lg overflow-hidden transition-shadow ${
    onClick ? 'cursor-pointer' : ''
  } ${isSelected ? 'shadow-sm' : 'hover:shadow-md'}`;

  return (
    <div
      className={`${baseClasses} ${className}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Desktop View */}
      <div className="hidden md:block">{desktopContent}</div>

      {/* Mobile View */}
      <div className="md:hidden">{mobileContent}</div>
    </div>
  );
};

export default ListRow;
